import type { Product } from '@haus-storefront-react/shared-types'
import { useRelatedProducts } from '@haus-storefront-react/vendure-plugin-configs/related-products'
import {
  Carousel,
  Spinner,
  Typography,
  useWidgetReadyAfterInitialLoad,
  WidgetReadyBoundary,
} from '@components'
import { getVendureProductId } from '../../utilities/utilities'
import type { RelatedProductsAttributes } from './_propsFn'
import { RelatedProductsItem } from './RelatedProductsItem'
import styles from './RelatedProducts.module.scss'
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useProduct } from '@haus-storefront-react/hooks'

const MOBILE_MEDIA_QUERY = '(max-width: 767px)'
const MOBILE_ITEMS_PER_VIEW = 2

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isProductArray(value: unknown): value is Product[] {
  return Array.isArray(value)
}

function getProductsFromResponse(response: unknown): Product[] {
  if (isProductArray(response)) {
    return response
  }

  if (!isRecord(response)) {
    return []
  }

  const items = response.items
  if (isProductArray(items)) {
    return items
  }

  const relatedProducts = response.relatedProducts
  if (isProductArray(relatedProducts)) {
    return relatedProducts
  }

  return []
}

/**
 * Render related products for the current PDP and display them inside the shared carousel component.
 */
const RelatedProducts = ({
  facetIds,
  itemsPerView,
  take,
  showRelatedProducts,
  showCta,
  showProgressBar,
  showInStock,
  showPrice,
  configStyles,
}: RelatedProductsAttributes) => {
  const { t } = useTranslation()
  const vendureProductId = getVendureProductId()
  const { data: product } = useProduct({ id: vendureProductId }, !!vendureProductId)
  const facetValueIds = product?.facetValues
    ?.map((facetValue) => {
      return facetIds?.includes(facetValue.facet.id) ? facetValue.id : undefined
    })
    .filter((id) => id !== undefined)
  const [isMobileViewport, setIsMobileViewport] = useState(false)
  const hasFacetFilters = Boolean(facetIds && facetIds.length > 0)
  const shouldFetchProducts = Boolean(vendureProductId) && (showRelatedProducts || hasFacetFilters)
  const effectiveItemsPerView = isMobileViewport
    ? Math.min(itemsPerView, MOBILE_ITEMS_PER_VIEW)
    : itemsPerView

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
    const updateViewportMode = (matches: boolean) => {
      setIsMobileViewport(matches)
    }

    updateViewportMode(mediaQuery.matches)

    const handleChange = (event: MediaQueryListEvent) => {
      updateViewportMode(event.matches)
    }

    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  const { data, error, isLoading } = useRelatedProducts({
    id: shouldFetchProducts ? vendureProductId : '',
    take,
    facetValueIds: hasFacetFilters ? facetValueIds : undefined,
  })

  const products = getProductsFromResponse(data)
  const shouldHideControls = products.length <= effectiveItemsPerView
  const shouldShowProgressBar = showProgressBar && !shouldHideControls
  const noProducts = shouldFetchProducts && !isLoading && products.length === 0
  const hasMissingProductContext = !vendureProductId
  const hasDisabledQuery = vendureProductId && !shouldFetchProducts
  const isReady = useWidgetReadyAfterInitialLoad({
    hasSettledContent:
      hasMissingProductContext ||
      hasDisabledQuery ||
      Boolean(error) ||
      noProducts ||
      products.length > 0,
    hasVisibleContent: true,
    isLoading,
  })

  return (
    <WidgetReadyBoundary isReady={isReady}>
      <section className={styles.root} aria-label={t('a11y.relatedProducts.aria.ariaLabel')}>
        {hasMissingProductContext ? (
          <div className={styles.status}>
            <Typography variant="body">{t('relatedProducts.missingProductContext')}</Typography>
          </div>
        ) : hasDisabledQuery ? (
          <div className={styles.status}>
            <Typography variant="body">{t('relatedProducts.disabledQuery')}</Typography>
          </div>
        ) : error ? (
          <div className={styles.status} role="alert">
            <Typography variant="body" className={styles.errorText}>
              {t('relatedProducts.error')}
            </Typography>
          </div>
        ) : isLoading ? (
          <div className={styles.status} aria-live="polite" aria-busy="true">
            <Spinner />
          </div>
        ) : noProducts ? (
          <div className={styles.status}>
            <Typography variant="body">{t('relatedProducts.empty')}</Typography>
          </div>
        ) : (
          <Carousel.Root
            itemsPerView={effectiveItemsPerView}
            ariaLabel={t('a11y.relatedProducts.aria.carouselAriaLabel')}
          >
            <Carousel.Controls
              className={classNames(styles.controls, shouldHideControls && styles.controlsHidden)}
            >
              <div className={styles.arrowControls}>
                <Carousel.Prev>
                  <ChevronLeftIcon aria-hidden width={20} height={20} />
                </Carousel.Prev>
                <Carousel.Next>
                  <ChevronRightIcon aria-hidden width={20} height={20} />
                </Carousel.Next>
              </div>
            </Carousel.Controls>

            <Carousel.Viewport>
              <Carousel.Track>
                {products.map((product) => (
                  <Carousel.Item key={product.id}>
                    <RelatedProductsItem
                      product={product}
                      showCta={showCta}
                      showInStock={showInStock}
                      showPrice={showPrice}
                      configStyles={configStyles}
                    />
                  </Carousel.Item>
                ))}
              </Carousel.Track>
            </Carousel.Viewport>
            {shouldShowProgressBar && <Carousel.Progress className={styles.progressBar} />}
          </Carousel.Root>
        )}
      </section>
    </WidgetReadyBoundary>
  )
}

export default RelatedProducts
